import { useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Banknote, Check, Tag, X, IdCard, Lock, MapPin, Pencil, ShieldCheck, Truck, User } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { useCart, useSite } from "@/context/Providers";
import { http, money } from "@/lib/api";

const PROFILE = "shadc_checkout_profile";
const EMPTY = { email: "", phone: "", first_name: "", last_name: "", address_1: "", address_2: "", postcode: "", note: "" };
const loadProfile = () => {
  try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(PROFILE)), note: "" }; } catch { return EMPTY; }
};

const RULES = {
  email: [(v) => /^\S+@\S+\.\S+$/.test(v), "Enter a valid email"],
  phone: [(v) => v.replace(/\D/g, "").length >= 10, "Enter a 10-digit phone number"],
  first_name: [(v) => v.trim().length > 0, "Required"],
  last_name: [(v) => v.trim().length > 0, "Required"],
  address_1: [(v) => v.trim().length >= 3, "Enter your street address"],
  postcode: [(v) => /^20[0-5]\d{2}$/.test(v.trim()), "We deliver within Washington, DC only (ZIP 200xx)"],
};
const STEPS = [
  { key: "contact", title: "Contact information", icon: User, fields: ["email", "phone"] },
  { key: "address", title: "Delivery address", icon: MapPin, fields: ["first_name", "last_name", "address_1", "postcode"] },
  { key: "payment", title: "Payment & confirmation", icon: Banknote, fields: [] },
];

const Input = ({ id, label, value, onChange, error, ...rest }) => (
  <div className={rest.wide ? "sm:col-span-2" : ""}>
    <label htmlFor={`co-${id}`} className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-gray-400">{label}</label>
    <input id={`co-${id}`} value={value} onChange={(e) => onChange(id, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `co-${id}-err` : undefined}
      {...Object.fromEntries(Object.entries(rest).filter(([k]) => k !== "wide"))}
      className={`h-12 w-full rounded-xl border bg-ink px-4 text-base text-white placeholder:text-gray-600 focus:outline-none ${error ? "border-red-500/70" : "border-line focus:border-gold"}`}
      data-testid={`checkout-${id}-input`} />
    {error && <p id={`co-${id}-err`} className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

const StepShell = ({ n, step, state, summary, onEdit, children }) => {
  const I = step.icon;
  return (
    <section className={`scroll-mt-28 rounded-2xl border bg-panel transition-colors ${state === "open" ? "border-gold/50" : "border-line"}`} aria-labelledby={`step-${step.key}`} data-testid={`checkout-step-${step.key}`}>
      <header className="flex items-center gap-4 p-5 sm:p-6">
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold ${state === "done" ? "bg-jade text-ink" : state === "open" ? "bg-gold text-ink" : "bg-raised text-gray-500"}`}>
          {state === "done" ? <Check className="h-4 w-4" aria-hidden /> : n}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={`step-${step.key}`} className={`flex items-center gap-2 text-lg font-semibold ${state === "locked" ? "text-gray-500" : "text-white"}`}><I className="h-4 w-4 text-gold" aria-hidden />{step.title}</h2>
          {state === "done" && <p className="mt-0.5 truncate text-sm text-gray-400">{summary}</p>}
        </div>
        {state === "done" && <button type="button" onClick={onEdit} className="flex items-center gap-1 text-sm font-semibold text-gold hover:underline" data-testid={`checkout-edit-${step.key}`}><Pencil className="h-3.5 w-3.5" aria-hidden />Edit</button>}
      </header>
      {state === "open" && <div className="border-t border-line p-5 sm:p-6">{children}</div>}
    </section>
  );
};

const PromoBox = ({ promo, onApply, onRemove }) => {
  const [code, setCode] = useState(() => localStorage.getItem("shadc_promo_code") || "");
  const [busy, setBusy] = useState(false);
  const apply = async (e) => { e.preventDefault(); if (!code.trim()) return; setBusy(true); await onApply(code.trim()); setBusy(false); };
  if (promo) return (
    <div className="mt-5 flex items-center justify-between rounded-xl border border-jade/40 bg-jade/10 px-4 py-3 text-sm" data-testid="checkout-promo-applied">
      <span className="flex items-center gap-2 text-jade-soft"><Tag className="h-4 w-4" aria-hidden /><strong>{promo.code}</strong> applied · {promo.percent}% off</span>
      <button type="button" onClick={onRemove} className="text-gray-400 hover:text-white" aria-label="Remove promo code" data-testid="checkout-promo-remove"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <form onSubmit={apply} className="mt-5 flex gap-2" data-testid="checkout-promo-form">
      <label htmlFor="co-promo" className="sr-only">Promo code</label>
      <input id="co-promo" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Promo code"
        className="h-11 min-w-0 flex-1 rounded-xl border border-line bg-ink px-4 text-sm uppercase tracking-wider text-white placeholder:normal-case placeholder:tracking-normal placeholder:text-gray-600 focus:border-gold focus:outline-none" data-testid="checkout-promo-input" />
      <button type="submit" disabled={busy} className="btn-ghost h-11 px-5 text-sm" data-testid="checkout-promo-apply">{busy ? "…" : "Apply"}</button>
    </form>
  );
};

const Summary = ({ items, subtotal, min, promo, onApply, onRemove, promoKey }) => (
  <aside className="h-fit rounded-2xl border border-line bg-panel p-6 lg:sticky lg:top-28" aria-labelledby="sum-title" data-testid="checkout-summary">
    <div className="flex items-center justify-between">
      <h2 id="sum-title" className="text-lg font-semibold text-white">Order summary</h2>
      <Link to="/cart/" className="text-sm text-gold hover:underline" data-testid="checkout-edit-cart-link">Edit cart</Link>
    </div>
    <ul className="mt-5 max-h-80 space-y-4 overflow-y-auto pr-1">
      {items.map((i) => (
        <li key={i.key} className="flex gap-3">
          <span className="relative shrink-0">
            <img src={i.image} alt="" width="64" height="64" className="h-16 w-16 rounded-xl bg-white object-contain p-1" />
            <span className="absolute -right-2 -top-2 grid h-6 min-w-6 place-items-center rounded-full bg-gold px-1 text-xs font-bold text-ink">{i.quantity}</span>
          </span>
          <span className="min-w-0 flex-1 text-sm">
            <span className="line-clamp-2 font-semibold text-white">{i.name}</span>
            {Object.values(i.options).length > 0 && <span className="block text-xs text-gray-400">{Object.values(i.options).join(" · ")}</span>}
          </span>
          <span className="text-sm font-semibold text-white">{money(i.unit_price * i.quantity)}</span>
        </li>
      ))}
    </ul>
    <PromoBox key={promoKey} promo={promo} onApply={onApply} onRemove={onRemove} />
    <dl className="mt-6 space-y-3 border-t border-line pt-5 text-sm">
      <div className="flex justify-between"><dt className="text-gray-400">Subtotal</dt><dd className="text-white">{money(subtotal)}</dd></div>
      {promo && <div className="flex justify-between"><dt className="text-gray-400">Discount ({promo.code})</dt><dd className="font-semibold text-jade-soft" data-testid="checkout-discount">−{money(promo.discount)}</dd></div>}
      <div className="flex justify-between"><dt className="text-gray-400">Delivery</dt><dd className={subtotal >= min ? "font-semibold text-jade-soft" : "text-gray-300"}>{subtotal >= min ? "Free" : "Confirmed by our team"}</dd></div>
      <div className="flex justify-between border-t border-line pt-4 text-base"><dt className="font-semibold text-white">Total due on delivery</dt><dd className="font-bold text-white" data-testid="checkout-total">{money(promo ? promo.total : subtotal)}</dd></div>
    </dl>
    {subtotal < min && <p className="mt-3 text-xs text-gray-500">Add {money(min - subtotal)} more for free delivery.</p>}
    <ul className="mt-6 space-y-2 border-t border-line pt-5 text-xs text-gray-400">
      <li className="flex gap-2"><Lock className="h-4 w-4 text-jade" aria-hidden />Encrypted, secure checkout</li>
      <li className="flex gap-2"><IdCard className="h-4 w-4 text-jade" aria-hidden />21+ ID verified at the door</li>
      <li className="flex gap-2"><Truck className="h-4 w-4 text-jade" aria-hidden />Delivering across Washington, DC</li>
    </ul>
  </aside>
);

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const site = useSite();
  const nav = useNavigate();
  const [f, setF] = useState(loadProfile);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(() => (loadProfile().email ? 1 : 0));
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [promo, setPromo] = useState(null);
  const [promoKey, setPromoKey] = useState(0);
  const idem = useMemo(() => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`), []);
  if (!items.length && !done) return <Navigate to="/cart/" replace />;
  const min = site?.free_delivery_minimum ?? 50;
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const next = (i) => {
    const errs = Object.fromEntries(STEPS[i].fields.filter((k) => !RULES[k][0](f[k])).map((k) => [k, RULES[k][1]]));
    setErrors(errs);
    if (Object.keys(errs).length) return;
    localStorage.setItem(PROFILE, JSON.stringify({ ...f, note: "" }));
    setStep(i + 1);
    setTimeout(() => document.querySelector(`[data-testid=checkout-step-${STEPS[i + 1].key}]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const cartBody = () => items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, options: i.options }));
  const applyPromo = async (code) => {
    try {
      const { data } = await http.post("/coupon/apply", { code, items: cartBody() });
      setPromo(data);
      localStorage.setItem("shadc_promo_code", data.code);
      toast.success(`${data.code} applied: you save ${money(data.discount)}`);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't apply that code.");
    }
  };
  const removePromo = () => { setPromo(null); setPromoKey((k) => k + 1); localStorage.removeItem("shadc_promo_code"); };

  const place = async () => {
    if (!age) return toast.error("Please confirm you are 21+ and will show a valid ID.");
    setBusy(true);
    try {
      const { data } = await http.post("/checkout", {
        idempotency_key: idem, age_confirmed: true, customer: { ...f, postcode: f.postcode.trim() },
        items: cartBody(), coupon: promo?.code,
      });
      localStorage.removeItem("shadc_promo_code");
      setDone(true);
      clear();
      nav("/order-confirmation/", { replace: true, state: { ...data, email: f.email } });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" && d) || d?.errors?.map((x) => x.error).join(" · ") || (Array.isArray(d) ? "Please check your details." : "We couldn't place your order. Please try again or call us."));
    } finally {
      setBusy(false);
    }
  };

  const stateOf = (i) => (i < step ? "done" : i === step ? "open" : "locked");
  const summaries = [`${f.email} · ${f.phone}`, `${f.first_name} ${f.last_name}, ${f.address_1}${f.address_2 ? ` ${f.address_2}` : ""}, Washington, DC ${f.postcode}`];

  return (
    <>
      <Seo title="Secure Checkout | SHADC Dispensary" description="Secure checkout" path="/checkout/" noindex />
      <div className="container-x py-10 sm:py-14">
        <div className="flex flex-wrap items-center gap-4">
          <ShieldCheck className="h-9 w-9 text-gold" aria-hidden />
          <div>
            <h1 className="text-3xl font-bold text-white sm:text-4xl" data-testid="checkout-title">Secure Checkout</h1>
            <p className="text-sm text-gray-400">Encrypted checkout · 21+ ID verified at the door · Pay cash on delivery</p>
          </div>
        </div>
        {site && !site.orders_enabled && (
          <p className="mt-6 rounded-2xl border border-gold/40 bg-gold/5 p-4 text-sm text-gold" role="note" data-testid="checkout-test-mode-notice">
            Test mode: orders placed here are validated but not yet sent to WooCommerce. They'll start appearing in WooCommerce once order sending is switched on.
          </p>
        )}
        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr]">
          <div className="min-w-0 space-y-4">
            <StepShell n={1} step={STEPS[0]} state={stateOf(0)} summary={summaries[0]} onEdit={() => setStep(0)}>
              <form onSubmit={(e) => { e.preventDefault(); next(0); }} className="grid gap-5 sm:grid-cols-2" noValidate>
                <Input id="email" label="Email" type="email" autoComplete="email" value={f.email} onChange={set} error={errors.email} placeholder="you@email.com" />
                <Input id="phone" label="Mobile phone" type="tel" autoComplete="tel" value={f.phone} onChange={set} error={errors.phone} placeholder="(202) 555-0123" />
                <p className="text-xs text-gray-500 sm:col-span-2">We'll text or call this number when your driver is on the way.</p>
                <button type="submit" className="btn-gold sm:col-span-2" data-testid="checkout-continue-contact">Continue to delivery</button>
              </form>
            </StepShell>
            <StepShell n={2} step={STEPS[1]} state={stateOf(1)} summary={summaries[1]} onEdit={() => setStep(1)}>
              <form onSubmit={(e) => { e.preventDefault(); next(1); }} className="grid gap-5 sm:grid-cols-2" noValidate>
                <Input id="first_name" label="First name (as on ID)" autoComplete="given-name" value={f.first_name} onChange={set} error={errors.first_name} />
                <Input id="last_name" label="Last name (as on ID)" autoComplete="family-name" value={f.last_name} onChange={set} error={errors.last_name} />
                <Input id="address_1" label="Street address" autoComplete="address-line1" value={f.address_1} onChange={set} error={errors.address_1} placeholder="2906 Georgia Ave NW" wide />
                <Input id="address_2" label="Apt / unit (optional)" autoComplete="address-line2" value={f.address_2} onChange={set} />
                <Input id="postcode" label="ZIP code" inputMode="numeric" maxLength={5} autoComplete="postal-code" value={f.postcode} onChange={set} error={errors.postcode} placeholder="20001" />
                <div className="sm:col-span-2 flex items-center gap-2 rounded-xl border border-line bg-raised px-4 py-3 text-sm text-gray-300"><MapPin className="h-4 w-4 text-gold" aria-hidden />Washington, DC — residential addresses only, no federal property</div>
                <div className="sm:col-span-2">
                  <label htmlFor="co-note" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-gray-400">Delivery notes (optional)</label>
                  <textarea id="co-note" rows={3} maxLength={500} value={f.note} onChange={(e) => set("note", e.target.value)} placeholder="Buzzer code, building entrance, where to meet…"
                    className="w-full rounded-xl border border-line bg-ink px-4 py-3 text-base text-white placeholder:text-gray-600 focus:border-gold focus:outline-none" data-testid="checkout-note-input" />
                </div>
                <button type="submit" className="btn-gold sm:col-span-2" data-testid="checkout-continue-address">Continue to payment</button>
              </form>
            </StepShell>
            <StepShell n={3} step={STEPS[2]} state={stateOf(2)}>
              <div className="space-y-5">
                <div className="flex items-center gap-4 rounded-xl border border-gold bg-gold/10 p-4" role="radio" aria-checked="true" data-testid="checkout-payment-cod">
                  <span className="grid h-5 w-5 place-items-center rounded-full border-2 border-gold"><span className="h-2.5 w-2.5 rounded-full bg-gold" /></span>
                  <Banknote className="h-5 w-5 text-gold" aria-hidden />
                  <span className="flex-1"><span className="block font-semibold text-white">Cash on delivery</span><span className="text-sm text-gray-400">Pay your driver in cash when your order arrives.</span></span>
                </div>
                <label className="flex cursor-pointer gap-3 rounded-xl border border-line p-4 text-sm text-gray-300 hover:border-gray-500">
                  <input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#D4AF37]" data-testid="checkout-age-checkbox" />
                  <span>I confirm I am <strong className="text-white">21 or older</strong> and will present a valid government-issued photo ID matching the name on this order. I agree to the <Link to="/terms/" className="text-gold underline">Terms</Link> and <Link to="/delivery-policy/" className="text-gold underline">Delivery Policy</Link>.</span>
                </label>
                <button type="button" onClick={place} disabled={busy} className="btn-gold w-full text-base" data-testid="checkout-place-order-button">
                  <Lock className="h-4 w-4" aria-hidden />{busy ? "Placing order…" : `Place order · ${money(promo ? promo.total : subtotal)}`}
                </button>
              </div>
            </StepShell>
          </div>
          <Summary items={items} subtotal={subtotal} min={min} promo={promo} promoKey={promoKey} onApply={applyPromo} onRemove={removePromo} />
        </div>
      </div>
    </>
  );
}

export function OrderConfirmation() {
  const { state } = useLocation();
  if (!state) return <Navigate to="/shop/" replace />;
  const test = state.mode !== "woocommerce";
  return (
    <>
      <Seo title="Order Received | SHADC Dispensary" description="Order confirmation" path="/order-confirmation/" noindex />
      <div className="container-x max-w-3xl py-14 text-center" data-testid="order-confirmation">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-jade/15"><Check className="h-8 w-8 text-jade" aria-hidden /></span>
        <h1 className="mt-6 text-4xl font-bold text-white sm:text-5xl">Thanks{state.first_name ? `, ${state.first_name}` : ""}! Order received.</h1>
        <p className="mt-4 text-gray-300">
          {test ? "TEST MODE — this order was validated but not sent to WooCommerce." : <>Your order <strong className="text-gold" data-testid="order-number">#{state.order_number}</strong> has been placed. A confirmation will be sent to {state.email}.</>}
        </p>
        <ul className="card-surface mx-auto mt-10 divide-y divide-line px-6 text-left" data-testid="order-lines">
          {state.lines.map((l) => (
            <li key={`${l.product_id}-${l.variation_id}`} className="flex justify-between gap-4 py-4 text-sm">
              <span className="text-white">{l.name} <span className="text-gray-500">× {l.quantity}</span>{Object.values(l.options).length > 0 && <span className="block text-xs text-gray-400">{Object.values(l.options).join(" · ")}</span>}</span>
              <span className="font-semibold">{money(l.line_total)}</span>
            </li>
          ))}
          {state.discount > 0 && <li className="flex justify-between py-4 text-sm text-jade-soft"><span>Discount ({state.coupon})</span><span data-testid="order-discount">−{money(state.discount)}</span></li>}
          <li className="flex justify-between py-4 text-base font-bold"><span>Total due on delivery (cash)</span><span data-testid="order-total">{money(state.total)}</span></li>
        </ul>
        <div className="card-surface mx-auto mt-6 grid gap-4 p-6 text-left text-sm text-gray-300 sm:grid-cols-3">
          <p className="flex gap-2"><Truck className="h-5 w-5 shrink-0 text-gold" aria-hidden />We'll contact you when your driver is on the way.</p>
          <p className="flex gap-2"><IdCard className="h-5 w-5 shrink-0 text-gold" aria-hidden />Have your 21+ photo ID ready.</p>
          <p className="flex gap-2"><Banknote className="h-5 w-5 shrink-0 text-gold" aria-hidden />Pay cash to the driver at handoff.</p>
        </div>
        <div className="mt-10 flex justify-center gap-3"><Link to="/shop/" className="btn-gold">Keep shopping</Link><Link to="/contact/" className="btn-ghost">Questions? Contact us</Link></div>
      </div>
    </>
  );
}
